import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Link,
  Card,
} from '@mui/material';
import { Clear, AssignmentTurnedIn, ContentPaste } from '@mui/icons-material';
import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../../contexts/AuthContext';
import type { ErrorObject } from '../../../../types/errorhandling.types';
import type { BrandingResponse } from '../../../../types/Branding.types';
import { getI18nValue } from '../../utils/i18nHelpers';
import { getColorConfigs } from './utils';
import { ColorPicker } from './components/ColorPicker';
import { LogoUpload } from './components/LogoUpload';
import { TextFieldWithHelp } from './components/TextFieldWithHelp';
import { StyledQuestionMark } from '../../../../components/QuestionMark';
import {
  StyledInputLabel,
  StyledSectionHeading,
} from '../../../../components/inputs/InputLabel';
import { AlertMessage } from '../../../../components/AlertMessage';

interface BrandingData {
  name: string;
  logoImage?: string;
  logoTag: string;
  logoLink: string;
  repositoryUrl: string;
  openWidgetSearchInNewWindow: boolean;
  vpatSearch: string;
  vpatAdmin: string;
}

interface BrandingColors {
  mainColor: string;
  headerColor: string;
  footerColor: string;
  secondaryColor: string;
  infoBoxColor: string;
  tableHeaderColor: string;
}

interface BrandingCallbacks {
  updateField: (field: keyof BrandingData, value: unknown) => void;
  updateColor: (color: keyof BrandingColors, value: string) => void;
  handleLogoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

type GeneralTabProps = {
  data: BrandingData;
  colors: BrandingColors;
  callbacks: BrandingCallbacks;
  readonly: boolean;
  fieldErrors?: ErrorObject;
  // Widget props
  account: string;
  publicUrl: string;
  branding?: BrandingResponse;
  i18nData?: Record<string, Record<string, unknown>>;
};

const GeneralTab = memo(
  ({
    data,
    colors,
    callbacks,
    readonly,
    fieldErrors = {},
    account,
    publicUrl,
    i18nData,
  }: GeneralTabProps) => {
    const { t } = useTranslation();
    const { loggedInAccount } = useAuthContext();
    const isAdmin = loggedInAccount === 'admin';
    const isViewingAdminBranding = isAdmin && account === 'admin';
    const isFieldDisabled = readonly;
    const colorConfigs = getColorConfigs(t);

    // Widget state
    const [searchTerm, setSearchTerm] = useState('');

    // Get user-defined search placeholder from i18n data
    const userSearchPlaceholder = i18nData
      ? getI18nValue(i18nData, 'en', 'search_placeholder')
      : '';
    const widgetPlaceholder =
      userSearchPlaceholder || t('branding.placeholders.search');
    const [copied, setCopied] = useState(false);

    const codeSnippet = `<div id="scfwid" data-account="${account}"></div>
<script src="${publicUrl}/assets/external/widget.js"></script>`;

    return (
      <>
        <Box>
          <Grid container spacing={{ xs: 3, md: 4, lg: 6 }}>
            {/* Left Column: Name, Logo and Colors */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: { xs: '100%', md: '90%', lg: '75%' },
                  pr: { xs: 0, md: 2 },
                  mt: 32,
                }}
              >
                {/* Name Section - Only show for admin users */}
                {isAdmin && (
                  <TextFieldWithHelp
                    name="name"
                    label={t('branding.fields.name')}
                    type="text"
                    value={data.name}
                    onChange={(e) =>
                      callbacks.updateField('name', e.target.value)
                    }
                    disabled={isFieldDisabled}
                    error={!!fieldErrors.name}
                    errorText={fieldErrors.name?.[0]}
                    onReset={() => callbacks.updateField('name', '')}
                  />
                )}

                {/* Logo Section */}
                <Box sx={{ mt: 32 }}>
                  <Typography
                    component="h2"
                    variant="h5"
                    gutterBottom
                    id="logo-section"
                  >
                    Logo
                  </Typography>

                  <LogoUpload
                    logoImage={data.logoImage}
                    onLogoChange={callbacks.handleLogoChange}
                    disabled={isFieldDisabled}
                    error={fieldErrors.logo_image}
                    headerColor={colors.headerColor}
                    required
                  />

                  <TextFieldWithHelp
                    name="logoTag"
                    label={t('branding.fields.logo_tag')}
                    type="text"
                    value={data.logoTag}
                    onChange={(e) =>
                      callbacks.updateField('logoTag', e.target.value)
                    }
                    disabled={isFieldDisabled}
                    error={!!fieldErrors.logo_tag}
                    errorText={fieldErrors.logo_tag?.[0]}
                    helpText={t('branding.help.logo_tag') as string}
                    onReset={() => callbacks.updateField('logoTag', '')}
                  />

                  <TextFieldWithHelp
                    name="logoLink"
                    label={t('branding.fields.logo_link')}
                    type="url"
                    value={data.logoLink}
                    onChange={(e) =>
                      callbacks.updateField('logoLink', e.target.value)
                    }
                    disabled={isFieldDisabled}
                    error={!!fieldErrors.logo_link}
                    errorText={fieldErrors.logo_link?.[0]}
                    helpText={t('branding.help.logo_link') as string}
                    onReset={() => callbacks.updateField('logoLink', '')}
                  />
                </Box>

                {/* Colors Section */}
                <Box sx={{ mt: 32 }}>
                  <Typography
                    component="h2"
                    variant="h5"
                    gutterBottom
                    id="colors-section"
                  >
                    Colors
                  </Typography>

                  {colorConfigs.map((config) => (
                    <ColorPicker
                      key={config.key}
                      config={config}
                      value={colors[config.key]}
                      onChange={(value) =>
                        callbacks.updateColor(config.key, value)
                      }
                      disabled={isFieldDisabled}
                    />
                  ))}

                  <AlertMessage severity="warning">
                    <Trans
                      i18nKey="branding.warnings.wcagContrast"
                      components={{
                        wcagStandards: (
                          <Link
                            href={t('branding.warnings.wcagLink') as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="always"
                          />
                        ),
                        contrastChecker: (
                          <Link
                            href={
                              t(
                                'branding.warnings.contrastCheckerLink'
                              ) as string
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="always"
                          />
                        ),
                      }}
                    />
                  </AlertMessage>
                </Box>
              </Box>
            </Grid>

            {/* Right Column: Settings */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: { xs: '100%', md: '90%', lg: '75%' },
                  pl: { xs: 0, md: 2 },
                  mt: 32,
                }}
              >
                {/* Widget Section */}
                <Box sx={{ mt: 32 }}>
                  <Typography
                    component="h2"
                    variant="h5"
                    gutterBottom
                    id="widget-section"
                  >
                    {t('branding.sections.widget')}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 3 }}>
                    Embed the SciFree search widget on your website to provide
                    direct access to your publications. The widget allows
                    visitors to search your repository without leaving your
                    site. For detailed installation instructions and
                    customization options, visit our{' '}
                    <Link
                      href="https://help.jst.scifree.se/hc/en-gb/articles/34766377470355-Amending-SciFree-Search-Widget"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      help documentation
                    </Link>
                    .
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={data.openWidgetSearchInNewWindow}
                          onChange={(e) =>
                            callbacks.updateField(
                              'openWidgetSearchInNewWindow',
                              e.target.checked
                            )
                          }
                          disabled={isFieldDisabled}
                        />
                      }
                      label={t(
                        'branding.fields.open_widget_search_in_new_window'
                      )}
                    />
                    <Tooltip
                      title={t(
                        'branding.help.open_widget_search_in_new_window'
                      )}
                      placement="top"
                    >
                      <IconButton size="small" sx={{ marginLeft: -12 }}>
                        <StyledQuestionMark fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Widget Preview */}
                <Box sx={{ my: 16 }}>
                  <StyledInputLabel htmlFor="widget-search-preview">
                    Preview:
                  </StyledInputLabel>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <TextField
                        id="widget-search-preview"
                        placeholder={widgetPlaceholder as string}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                          minWidth: 250,
                        }}
                        InputProps={{
                          endAdornment: searchTerm && (
                            <IconButton
                              size="small"
                              onClick={() => setSearchTerm('')}
                            >
                              <Clear />
                            </IconButton>
                          ),
                        }}
                      />
                    </Box>
                    <Button
                      variant="primary"
                      onClick={() =>
                        window.open(
                          `${publicUrl}/${account}?term=${searchTerm}`,
                          '_blank'
                        )
                      }
                      sx={{
                        backgroundColor: colors.mainColor,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: colors.mainColor,
                        },
                      }}
                    >
                      {t('branding.buttons.search')}
                    </Button>
                  </Box>
                </Box>

                {/* Code Snippet */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <StyledSectionHeading>Implementation:</StyledSectionHeading>
                    <Button
                      endIcon={
                        copied ? <AssignmentTurnedIn /> : <ContentPaste />
                      }
                      onClick={() => {
                        navigator.clipboard.writeText(codeSnippet);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      size="small"
                      variant="default"
                    >
                      {copied
                        ? t('branding.buttons.copied')
                        : t('branding.buttons.copyCode')}
                    </Button>
                  </Box>
                  <Card variant="secondary">
                    <code>{codeSnippet}</code>
                  </Card>
                </Box>

                {/* Agreements Section */}
                <Box sx={{ mt: 32 }}>
                  <Typography
                    component="h2"
                    variant="h5"
                    gutterBottom
                    id="agreements-section"
                  >
                    {t('branding.sections.agreements')}
                  </Typography>

                  <TextFieldWithHelp
                    name="repositoryUrl"
                    label={t('branding.fields.repo_url')}
                    type="url"
                    value={data.repositoryUrl}
                    onChange={(e) =>
                      callbacks.updateField('repositoryUrl', e.target.value)
                    }
                    disabled={isFieldDisabled}
                    error={!!fieldErrors.repo_url}
                    errorText={fieldErrors.repo_url?.[0]}
                    helpText={t('branding.help.repo_url') as string}
                    onReset={() => callbacks.updateField('repositoryUrl', '')}
                  />
                </Box>

                {isViewingAdminBranding && (
                  <Box sx={{ mt: 32 }}>
                    <Typography
                      component="h2"
                      variant="h5"
                      gutterBottom
                      id="vpat-section"
                    >
                      {t('branding.sections.vpat')}
                    </Typography>

                    <TextFieldWithHelp
                      name="vpatSearch"
                      label={t('branding.fields.vpat_search')}
                      type="url"
                      value={data.vpatSearch}
                      onChange={(e) =>
                        callbacks.updateField('vpatSearch', e.target.value)
                      }
                      disabled={isFieldDisabled}
                      error={!!fieldErrors.vpat_search}
                      errorText={fieldErrors.vpat_search?.[0]}
                      helpText={t('branding.help.vpat_search') as string}
                      onReset={() => callbacks.updateField('vpatSearch', '')}
                    />

                    <TextFieldWithHelp
                      name="vpatAdmin"
                      label={t('branding.fields.vpat_admin')}
                      type="url"
                      value={data.vpatAdmin}
                      onChange={(e) =>
                        callbacks.updateField('vpatAdmin', e.target.value)
                      }
                      disabled={isFieldDisabled}
                      error={!!fieldErrors.vpat_admin}
                      errorText={fieldErrors.vpat_admin?.[0]}
                      helpText={t('branding.help.vpat_admin') as string}
                      onReset={() => callbacks.updateField('vpatAdmin', '')}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </>
    );
  }
);

GeneralTab.displayName = 'GeneralTab';

export default GeneralTab;
